import {
  Input as ChakraInput,
  InputGroup,
  type InputProps as ChakraInputProps,
} from '@chakra-ui/react';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { CancelIcon } from 'assets/icons/v2';
import {
  cloneElement,
  forwardRef,
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement,
  type Ref,
} from 'react';

export type NovaInputProps = ChakraInputProps & {
  leftElement?: JSX.Element;
  rightElement?: JSX.Element;
  /**
   * Clear control when focused or non-empty. Uncontrolled: clears via DOM + `onChange`.
   * Controlled (`value`): also pass `onClear` to update parent state.
   */
  clearable?: boolean;
  onClear?: () => void;
};

export const Input = forwardRef<HTMLInputElement, NovaInputProps>((props, ref): ReactElement => {
  const {
    leftElement,
    rightElement,
    clearable = false,
    onClear,
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    disabled,
    id: idProp,
    variant = 'primary',
    size = 'md',
    ref: refFromRegister,
    ...rest
  } = props as NovaInputProps & { ref?: Ref<HTMLInputElement> };

  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const inputNode = useRef<{ el: HTMLInputElement | null }>({ el: null });
  const [focused, setFocused] = useState(false);
  const [uncontrolledNonEmpty, setUncontrolledNonEmpty] = useState(
    () => defaultValue !== undefined && String(defaultValue).length > 0
  );

  const isControlled = 'value' in props;
  const hasValue = isControlled ? String(value ?? '').length > 0 : uncontrolledNonEmpty;
  const canClear =
    clearable &&
    !disabled &&
    !rightElement &&
    (focused || hasValue) &&
    (isControlled ? Boolean(onClear) : true);

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      inputNode.current.el = node;
      if (typeof refFromRegister === 'function') {
        refFromRegister(node);
      } else if (refFromRegister) {
        (refFromRegister as { current: HTMLInputElement | null }).current = node;
      }
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as { current: HTMLInputElement | null }).current = node;
      }
    },
    [ref, refFromRegister]
  );

  const handleClear = useCallback(() => {
    if (isControlled) {
      onClear?.();
      return;
    }
    const { el } = inputNode.current;
    if (el) {
      el.value = '';
      setUncontrolledNonEmpty(false);
      if (onChange) {
        onChange({
          target: el,
          currentTarget: el,
        } as ChangeEvent<HTMLInputElement>);
      }
    }
    onClear?.();
  }, [isControlled, onChange, onClear]);

  const endContent = (() => {
    if (canClear) {
      return (
        <NovaIconButton
          type="button"
          variant="primary"
          size="s"
          aria-label="Eingabe löschen"
          onPointerDown={(e) => {
            e.preventDefault();
            handleClear();
          }}
          icon={CancelIcon}
        />
      );
    }
    if (rightElement) {
      return rightElement;
    }
    return undefined;
  })();

  let valueProps:
    | Pick<ChakraInputProps, 'value'>
    | Pick<ChakraInputProps, 'defaultValue'>
    | Record<string, never>;
  if (isControlled) {
    valueProps = { value: value ?? '' };
  } else if (defaultValue !== undefined) {
    valueProps = { defaultValue };
  } else {
    valueProps = {};
  }

  return (
    <InputGroup
      data-disabled={disabled ? '' : undefined}
      gap={0}
      width="full"
      css={{
        '&:not([data-disabled]):focus-within > * > svg': {
          color: 'icon.accent',
        },
        '&[data-disabled] > * > svg': {
          color: 'icon.tertiary',
          opacity: 'disabled',
        },
      }}
      startElement={
        leftElement
          ? cloneElement(leftElement, {
              color: 'icon.quaternary',
              boxSize: 'icon.s',
              glyphSize: 's',
              ...leftElement.props,
            })
          : undefined
      }
      endElement={endContent}
    >
      <ChakraInput
        ref={setRefs}
        id={inputId}
        variant={variant}
        size={size}
        {...valueProps}
        disabled={disabled}
        onChange={(e) => {
          if (!isControlled && clearable) {
            setUncontrolledNonEmpty(e.target.value.length > 0);
          }
          onChange?.(e);
        }}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        textOverflow="ellipsis"
        {...rest}
      />
    </InputGroup>
  );
});

Input.displayName = 'Input';
